import type { ReactNode } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Lock,
  RotateCcw,
  Share,
} from 'lucide-react';

import { BrandAvatar } from './BrandAvatar';
import { ExpandableCaption } from './ExpandableCaption';
import { PhoneFrame, StatusBar } from './PhoneFrame';
import { PreviewMedia } from './PreviewMedia';

interface WebsitePreviewProps {
  caption: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
  mediaSlot?: ReactNode;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
}

export const WebsitePreview = ({
  caption,
  mediaUrl,
  mediaType,
  mediaSlot,
  displayName,
  handle,
  avatarUrl,
}: WebsitePreviewProps) => {
  // eslint-disable-next-line i18next/no-literal-string -- placeholder handle forming the mock domain
  const domain = `${handle || 'yourbrand'}.com`;

  return (
    <PhoneFrame bgcolor="#fff" scrollable>
      <StatusBar tone="dark" />

      <div className="flex items-center gap-1 px-2 py-1.5 bg-[#f2f2f7] border-b border-black/10">
        <div className="flex-1 flex items-center gap-1 px-2 py-1 bg-white rounded border border-black/10">
          <Lock size={11} color="rgba(0,0,0,0.55)" />
          <span className="text-[11px] text-black/70">{domain}</span>
          <div className="ml-auto flex items-center">
            <RotateCcw size={11} color="rgba(0,0,0,0.45)" />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 py-2 border-b border-black/10">
        <BrandAvatar avatarUrl={avatarUrl} size={28} alt={displayName} />
        <span className="text-sm font-bold">{displayName}</span>
      </div>

      <div
        data-testid="phone-preview-media-frame"
        className="relative flex shrink-0 items-center justify-center bg-gray-100"
      >
        {mediaSlot ? (
          // Variant cards inject their own media, sized to fill a fixed box.
          <div className="relative h-72 w-full">{mediaSlot}</div>
        ) : mediaUrl ? (
          // Uploaded media shows at its natural aspect (never cropped), capped so a
          // tall image doesn't dominate the frame; extremes letterbox instead.
          <PreviewMedia
            mediaUrl={mediaUrl}
            mediaType={mediaType}
            className="block w-full h-auto max-h-[385px] object-contain"
          />
        ) : (
          <div className="flex h-72 w-full items-center justify-center">
            <ImageIcon size={36} color="rgba(0,0,0,0.35)" />
          </div>
        )}
      </div>

      <div className="px-3 py-2.5">
        <ExpandableCaption
          data-testid="phone-preview-caption"
          className="text-[13px] leading-[1.4]"
        >
          {caption}
        </ExpandableCaption>
      </div>

      <div className="flex items-center justify-around py-1.5 border-t border-black/10 bg-[#f2f2f7] text-black/55">
        <ChevronLeft size={18} />
        <ChevronRight size={18} />
        <Share size={18} />
      </div>
    </PhoneFrame>
  );
};

WebsitePreview.displayName = 'WebsitePreview';
